<template>
  <div class="animate-fade-in">
    <div class="bg-white rounded-[2.5rem] shadow-xl overflow-hidden border border-gray-100">
      
      <div class="p-6 border-b bg-gray-50 flex flex-col md:flex-row justify-between items-center gap-4">
        
        <div class="flex items-center gap-4 w-full md:w-auto">
          <h2 class="text-2xl font-black text-carpatina-gri uppercase flex items-center gap-3">
            <Mail class="text-carpatina-portocaliu" :size="28" /> 
            Inbox
          </h2>
          <span class="bg-carpatina-verde text-white px-3 py-1 rounded-full text-xs font-bold shadow-sm">
            {{ filteredContacte.length }}
          </span>
        </div>

        <div class="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          
          <div class="flex bg-white p-1 rounded-xl shadow-sm border border-gray-200">
            <button @click="filterStatus = 'all'" 
                    :class="filterStatus === 'all' ? 'bg-carpatina-gri text-white shadow-md' : 'text-gray-400 hover:text-carpatina-gri'"
                    class="px-4 py-2 rounded-lg text-xs font-black uppercase transition-all">
              Toate
            </button>
            <button @click="filterStatus = 'unread'" 
                    :class="filterStatus === 'unread' ? 'bg-carpatina-portocaliu text-white shadow-md' : 'text-gray-400 hover:text-carpatina-portocaliu'"
                    class="px-4 py-2 rounded-lg text-xs font-black uppercase transition-all flex items-center gap-1">
              Noi <span v-if="unreadCount > 0" class="w-2 h-2 bg-white rounded-full animate-pulse"></span>
            </button>
            <button @click="filterStatus = 'read'" 
                    :class="filterStatus === 'read' ? 'bg-green-600 text-white shadow-md' : 'text-gray-400 hover:text-green-600'"
                    class="px-4 py-2 rounded-lg text-xs font-black uppercase transition-all">
              Arhivă
            </button>
          </div>

          <div class="relative flex-grow md:flex-grow-0 md:w-64">
            <input v-model="searchQuery" type="text" placeholder="Caută client..." 
                   class="w-full pl-10 pr-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-carpatina-portocaliu outline-none text-sm font-bold transition">
            <Search :size="18" class="absolute left-3 top-3 text-gray-400" />
          </div>

        </div>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-carpatina-gri text-white text-xs uppercase tracking-widest">
              <th class="p-4">Data</th>
              <th class="p-4">Client</th>
              <th class="p-4">Contact</th>
              <th class="p-4 w-1/3">Mesaj</th>
              <th class="p-4 text-center">Acțiuni</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="c in filteredContacte" :key="c.id" 
                :class="!c.citit ? 'bg-orange-50/50' : 'hover:bg-gray-50'"
                class="transition-colors group">
              
              <td class="p-4 text-xs font-bold text-gray-400 whitespace-nowrap">
                {{ formatData(c.data_creare) }}
              </td>

              <td class="p-4">
                <div class="flex items-center gap-2">
                  <div :class="!c.citit ? 'bg-carpatina-portocaliu text-white' : 'bg-gray-100 text-gray-400'"
                       class="p-2 rounded-lg transition-colors">
                    <User :size="16" />
                  </div>
                  <span class="font-black text-carpatina-gri text-sm">{{ c.nume_client }}</span>
                  <span v-if="!c.citit" class="text-[10px] bg-red-500 text-white px-2 rounded-full font-bold uppercase">Nou</span>
                </div>
              </td>

              <td class="p-4">
                <div class="space-y-1">
                  <a :href="'tel:' + c.telefon" class="flex items-center gap-1 text-sm font-bold text-carpatina-portocaliu hover:underline">
                    <Phone :size="14" /> {{ c.telefon }}
                  </a>
                  <div v-if="c.email" class="flex items-center gap-1 text-xs text-gray-500 italic">
                    <AtSign :size="12" /> {{ c.email }}
                  </div>
                </div>
              </td>

              <td class="p-4">
                <p class="text-sm text-gray-600 leading-relaxed italic line-clamp-2 hover:line-clamp-none transition-all cursor-pointer" :title="c.mesaj">
                  "{{ c.mesaj }}"
                </p>
              </td>

              <td class="p-4 text-center">
                <div class="flex justify-center gap-2">
                  <button @click="marcheazaCitit(c)" 
                          v-if="!c.citit"
                          title="Marchează ca citit"
                          class="p-2 bg-green-50 text-green-600 rounded-xl hover:bg-green-500 hover:text-white transition shadow-sm border border-green-100">
                    <CheckCircle :size="18" />
                  </button>
                  <button @click="stergeContact(c.id)" 
                          title="Șterge mesaj"
                          class="p-2 bg-red-50 text-red-500 rounded-xl hover:bg-red-500 hover:text-white transition shadow-sm border border-red-100">
                    <Trash2 :size="18" />
                  </button>
                </div>
              </td>
            </tr>

            <tr v-if="filteredContacte.length === 0">
              <td colspan="5" class="p-20 text-center">
                <div class="flex flex-col items-center opacity-30 text-gray-400">
                  <Inbox :size="64" />
                  <p class="text-xl font-bold uppercase mt-4">Nu am găsit mesaje</p>
                  <button @click="resetFilters" class="text-xs text-carpatina-portocaliu font-bold mt-2 hover:underline">
                    Șterge filtrele
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import Swal from 'sweetalert2';
import { 
  Mail, Phone, User, AtSign, Trash2, CheckCircle, Inbox, Search, 
} from 'lucide-vue-next';

// STATE
const contacte = ref([]);
const searchQuery = ref('');
const filterStatus = ref('all'); // 'all', 'unread', 'read'

// LOGICĂ FILTRARE (Aici e șmecheria)
const filteredContacte = computed(() => {
  return contacte.value.filter(c => {
    // 1. Filtru Text
    const query = searchQuery.value.toLowerCase();
    const matchSearch = !query || 
                        c.nume_client.toLowerCase().includes(query) || 
                        c.telefon.includes(query) || 
                        (c.email && c.email.toLowerCase().includes(query));

    // 2. Filtru Status (Nou / Arhivă)
    let matchStatus = true;
    if (filterStatus.value === 'unread') matchStatus = !c.citit;
    if (filterStatus.value === 'read') matchStatus = c.citit;

    return matchSearch && matchStatus;
  });
});

const unreadCount = computed(() => contacte.value.filter(c => !c.citit).length);

const fetchContacte = async () => {
  try {
    const res = await axios.get('/admin/contacte');
    contacte.value = res.data;
  } catch (err) {
    console.error("Eroare la incarcare contacte", err);
  }
};

const formatData = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('ro-RO', { 
    day: '2-digit', 
    month: 'short', 
    year: 'numeric',
    hour: '2-digit', 
    minute: '2-digit'
  });
};

const marcheazaCitit = async (contact) => {
  try {
    await axios.patch(`/admin/contact/${contact.id}/citit`);
    contact.citit = true; // Update local
  } catch (err) {
    Swal.fire('Eroare', 'Nu s-a putut actualiza statusul', 'error');
  }
};

const stergeContact = async (id) => {
  const result = await Swal.fire({
    title: 'Ștergi mesajul?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    confirmButtonText: 'Da, șterge!',
    cancelButtonText: 'Nu'
  });

  if (result.isConfirmed) {
    try {
      await axios.delete(`/admin/contact/${id}`);
      contacte.value = contacte.value.filter(c => c.id !== id);
      Swal.fire({ title: 'Șters!', icon: 'success', timer: 1000, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Eroare', 'Nu s-a putut șterge mesajul', 'error');
    }
  }
};

const resetFilters = () => {
    searchQuery.value = '';
    filterStatus.value = 'all';
}

onMounted(fetchContacte);
</script>

<style scoped>
.animate-fade-in { animation: fadeIn 0.4s ease-out; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>